import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase,
  Heart,
  Coins,
  Brain,
  Flame,
  Compass,
  Check,
  ArrowRight,
  Play,
  Wand2,
  Share2,
} from "lucide-react";
import { useEffect, useRef } from "react";
import Navbar from "@/components/Navbar";
import HeroCosmos from "@/components/HeroCosmos";
import FloatingClarityCard from "@/components/FloatingClarityCard";
import { track } from "@/lib/analytics";
import { useLang } from "@/lib/i18n";

const FEATURES = [
  { icon: Briefcase, key: "career" },
  { icon: Heart, key: "relationship" },
  { icon: Coins, key: "finance" },
  { icon: Brain, key: "emotion" },
  { icon: Flame, key: "spiritual" },
  { icon: Compass, key: "purpose" },
];

const PACKS = [
  {
    id: "pack_1",
    price: 299,
    questions: 1,
    recommended: false,
  },
  {
    id: "pack_3",
    price: 599,
    questions: 3,
    recommended: true,
    saveKey: "298",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    // Always fire a landing visit event
    try {
      track("visit_landing");
    } catch (_) {}
    // Fire referral_visit only if this load came via a UTM-tagged link
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("utm_source")) {
        track("referral_visit", {
          utm_source: sp.get("utm_source"),
          utm_medium: sp.get("utm_medium"),
          utm_campaign: sp.get("utm_campaign"),
        });
      }
    } catch (_) {}
  }, []);

  return (
    <div className="min-h-screen text-slate-100 cosmic-bg">
      <Navbar />

      {/* HERO */}
      <section className="relative">
        <HeroCosmos>
          <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-36">
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-10 lg:gap-12 items-center">
              {/* LEFT — copy */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="lg:col-span-6"
              >
                {/* Premium badge */}
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase mb-8 relative"
                  style={{
                    background:
                      "linear-gradient(120deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 50%, rgba(212,175,55,0.18) 100%)",
                    border: "1px solid rgba(212,175,55,0.45)",
                    color: "#FBE38E",
                    boxShadow:
                      "0 0 30px rgba(212,175,55,0.18), inset 0 0 12px rgba(212,175,55,0.10)",
                  }}
                  data-testid="hero-premium-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]" />
                  {t("brand.tagline")}
                </motion.div>

                {/* Headline — ~8% larger with balanced wrap for premium feel */}
                <motion.h1
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.0, delay: 0.15, ease: "easeOut" }}
                  className="font-serif text-[3.2rem] sm:text-[3.8rem] md:text-[4.4rem] lg:text-[82px] leading-[1.05] font-light tracking-tight text-white [text-wrap:balance]"
                  data-testid="hero-headline"
                >
                  {t("hero.headline.a")} {t("hero.headline.b")}{" "}
                  <span className="shimmer-gold italic whitespace-nowrap">{t("hero.headline.italic")}</span>
                </motion.h1>

                {/* Subheadline — refined spacing */}
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.4 }}
                  className="mt-9 md:mt-11 report-body text-lg md:text-xl text-slate-300/95 max-w-xl leading-relaxed"
                  data-testid="hero-subheadline"
                >
                  {t("hero.subheadline")}
                </motion.p>

                {/* Supporting line — even shorter */}
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.55 }}
                  className="mt-5 text-sm md:text-base text-slate-400 font-light max-w-xl"
                  data-testid="hero-supporting"
                >
                  {t("hero.supporting")}
                </motion.p>

                {/* Trust badges — 4 small pills */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.65 }}
                  className="mt-7 md:mt-8 flex flex-wrap gap-2 md:gap-2.5 max-w-xl"
                  data-testid="hero-trust-badges"
                >
                  {[
                    "hero.trust.personalised",
                    "hero.trust.wisdom",
                    "hero.trust.private",
                    "hero.trust.psychology",
                  ].map((k) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] md:text-xs font-medium text-slate-200/95 backdrop-blur-[2px] transition-colors hover:border-[rgba(212,175,55,0.45)]"
                      style={{
                        background: "rgba(212,175,55,0.06)",
                        border: "1px solid rgba(212,175,55,0.22)",
                      }}
                      data-testid={`hero-trust-${k.replace("hero.trust.", "")}`}
                    >
                      <span className="w-1 h-1 rounded-full bg-[#D4AF37] shadow-[0_0_6px_#D4AF37]" />
                      {t(k)}
                    </span>
                  ))}
                </motion.div>

                {/* CTAs — refined breathing */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.75 }}
                  className="mt-8 md:mt-10 flex flex-wrap gap-4"
                >
                  <button
                    onClick={() => navigate("/language")}
                    className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium transition-transform hover:scale-[1.03] cta-gold"
                    data-testid="hero-start-reading-button"
                  >
                    {t("hero.cta.start")}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <a
                    href="#features"
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium border border-white/20 hover:bg-white/5 hover:border-[#D4AF37]/50 transition-colors"
                    data-testid="hero-watch-demo-button"
                  >
                    <Play className="w-4 h-4" />
                    {t("hero.cta.watch")}
                  </a>
                </motion.div>

                {/* Premium positioning statement */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.85 }}
                  className="mt-10 font-serif italic text-lg md:text-xl text-slate-200 leading-snug"
                  data-testid="hero-positioning"
                >
                  {t("hero.positioning.a")}<br />
                  <span className="gold-text">{t("hero.positioning.b")}</span>
                </motion.div>
              </motion.div>

              {/* RIGHT — floating clarity card */}
              <div className="lg:col-span-4">
                <FloatingClarityCard />
              </div>
            </div>
          </div>
        </HeroCosmos>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-2xl mb-16">
          <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">{t("features.eyebrow")}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-white">
            {t("features.title.a")}<br />{t("features.title.b")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => {
            const title = t(`feature.${f.key}.title`);
            const q = t(`feature.${f.key}.q`);
            return (
              <motion.div
                key={f.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="glass rounded-3xl p-8 hover:-translate-y-1 transition-all duration-300 hover:shadow-2xl hover:border-[#D4AF37]/30 group"
                data-testid={`feature-card-${f.key}`}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-colors"
                  style={{ background: "rgba(212,175,55,0.1)" }}
                >
                  <f.icon className="w-5 h-5" style={{ color: "#D4AF37" }} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl font-medium text-white mb-3">
                  {title}
                </h3>
                <p className="text-slate-400 italic font-light text-base leading-relaxed">
                  {`"${q}"`}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* APPROACH */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">{t("approach.eyebrow")}</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-white leading-tight">
                {t("approach.title.a")}<br />
                <span className="italic gold-text">{t("approach.title.b")}</span>
              </h2>
              <p className="mt-8 text-lg text-slate-300/90 leading-relaxed font-light">
                {t("approach.body")}
              </p>
              <ul className="mt-10 space-y-4">
                {[
                  t("approach.item.1"),
                  t("approach.item.2"),
                  t("approach.item.3"),
                  t("approach.item.4"),
                  t("approach.item.5"),
                  t("approach.item.6"),
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-slate-300">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#D4AF37" }} strokeWidth={1.5} />
                    <span className="font-light">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="glass rounded-3xl p-10 gold-glow">
                <div className="text-xs tracking-[0.25em] uppercase gold-text mb-6">
                  {t("approach.sample.eyebrow")}
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-28 h-28 rounded-full border-2 flex items-center justify-center" style={{ borderColor: "#D4AF37" }}>
                      <span className="font-serif text-4xl text-white">82</span>
                    </div>
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.2em] uppercase gold-text bg-[#0B1020] px-2">
                      {t("approach.sample.clarity")}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="text-xs tracking-[0.2em] uppercase text-slate-400">{t("approach.sample.emotion.label")}</div>
                    <p className="text-slate-100 font-light leading-snug">
                      {t("approach.sample.emotion.body")}
                    </p>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-white/5 space-y-4">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase gold-text">{t("approach.sample.blind.label")}</div>
                    <p className="text-sm text-slate-300 mt-1.5 font-light">
                      {t("approach.sample.blind.body")}
                    </p>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase gold-text">{t("approach.sample.action.label")}</div>
                    <p className="text-sm text-slate-300 mt-1.5 font-light">
                      {t("approach.sample.action.body")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GROWTH SPOTLIGHT — Clarity Card & Simulator */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div
            className="lg:col-span-3 rounded-3xl p-10 relative overflow-hidden"
            style={{
              background: "linear-gradient(160deg, rgba(212,175,55,0.10), rgba(49,46,129,0.20))",
              border: "1px solid rgba(212,175,55,0.25)",
            }}
            data-testid="growth-clarity-card"
          >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase gold-text mb-4">
              <Share2 className="w-3.5 h-3.5" />
              {t("growth.card.eyebrow")}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-white leading-tight">
              {t("growth.card.title.a")}<br />
              <span className="italic gold-text">{t("growth.card.title.b")}</span> {t("growth.card.title.c")}
            </h2>
            <p className="mt-6 text-slate-300 font-light max-w-xl">
              {t("growth.card.body")}
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-300">
              <div><span className="font-serif text-3xl text-white">3</span> {t("growth.card.stat.channels")}</div>
              <div><span className="font-serif text-3xl text-white">∞</span> {t("growth.card.stat.shares")}</div>
              <div><span className="font-serif text-3xl gold-text">0₹</span> {t("growth.card.stat.free")}</div>
            </div>
            <button
              onClick={() => navigate("/language")}
              className="mt-10 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium gold-glow"
              style={{ background: "#D4AF37", color: "#0B1020" }}
              data-testid="growth-card-cta"
            >
              {t("growth.card.cta")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div
            className="lg:col-span-2 glass rounded-3xl p-10 flex flex-col"
            data-testid="growth-simulator"
          >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase gold-text mb-4">
              <Wand2 className="w-3.5 h-3.5" />
              {t("growth.sim.eyebrow")}
            </div>
            <h3 className="font-serif text-3xl font-light text-white leading-tight">
              {t("growth.sim.title.a")}<br />{t("growth.sim.title.b")}
            </h3>
            <p className="mt-5 text-slate-300 font-light flex-1">
              {t("growth.sim.body")}
            </p>
            <Link
              to="/simulator"
              className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border border-white/20 hover:bg-white/5 text-white w-fit"
              data-testid="growth-simulator-cta"
            >
              {t("growth.sim.cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-center mb-16">
          <div className="text-xs tracking-[0.25em] uppercase gold-text mb-4">{t("pricing.eyebrow")}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-white">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-slate-400 text-sm md:text-base font-light max-w-xl mx-auto">
            {t("pricing.subtitle")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="glass rounded-3xl p-8 md:p-10" data-testid="pricing-free">
            <div className="text-sm tracking-[0.2em] uppercase text-slate-400 mb-2">{t("pricing.free.label")}</div>
            <div className="flex items-baseline gap-2 mb-1">
              <div className="font-serif text-5xl text-white">₹0</div>
            </div>
            <div className="text-slate-400 text-sm mb-8">{t("pricing.free.oneEach")}</div>
            <ul className="space-y-3 mb-10 text-slate-300 font-light text-sm">
              <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.free.item.1")}</li>
              <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.free.item.2")}</li>
              <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.free.item.3")}</li>
            </ul>
            <button
              onClick={() => navigate("/language")}
              className="w-full rounded-full py-3.5 text-sm font-medium border border-white/20 hover:bg-white/5 transition-colors text-white"
              data-testid="pricing-free-cta"
            >
              {t("pricing.free.cta")}
            </button>
          </div>

          {PACKS.map((pack) => {
            const isRec = pack.recommended;
            const packLabel = pack.questions === 1 ? t("pricing.pack.oneQ") : t("pricing.pack.nQ", { n: pack.questions });
            return (
              <div
                key={pack.id}
                className={`rounded-3xl p-8 md:p-10 relative ${isRec ? "gold-glow" : "glass"}`}
                style={
                  isRec
                    ? {
                        background:
                          "linear-gradient(180deg, rgba(212,175,55,0.12), rgba(49,46,129,0.18))",
                        border: "1px solid rgba(212,175,55,0.4)",
                      }
                    : undefined
                }
                data-testid={`pricing-${pack.id}`}
              >
                {isRec && (
                  <div
                    className="absolute -top-3 left-10 px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded-full"
                    style={{ background: "#D4AF37", color: "#0B1020" }}
                  >
                    {t("pricing.recommended")}
                  </div>
                )}
                <div className={`text-sm tracking-[0.2em] uppercase mb-2 ${isRec ? "gold-text" : "text-slate-400"}`}>
                  {packLabel}
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <div className="font-serif text-5xl text-white">₹{pack.price}</div>
                  {pack.saveKey && (
                    <div className="text-[11px] tracking-[0.2em] uppercase gold-text">
                      {lang === "hi" ? `₹${pack.saveKey} की बचत` : `Save ₹${pack.saveKey}`}
                    </div>
                  )}
                </div>
                <div className="text-slate-400 text-sm mb-8">
                  {pack.questions === 1 ? t("pw.pack.1q") : t("pw.pack.3q")}
                </div>
                <ul className="space-y-3 mb-10 text-slate-200 font-light text-sm">
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.pack.item.1")}</li>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.pack.item.2")}</li>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.pack.item.3")}</li>
                  <li className="flex gap-3"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#D4AF37" }} />{t("pricing.pack.item.4")}</li>
                </ul>
                <button
                  onClick={() => navigate("/paywall")}
                  className={`w-full rounded-full py-3.5 text-sm font-medium transition-colors ${
                    isRec ? "cta-gold" : "border border-white/20 hover:bg-white/5 text-white"
                  }`}
                  data-testid={`pricing-${pack.id}-cta`}
                >
                  {pack.questions === 1
                    ? t("pricing.pack.getOne", { price: pack.price })
                    : t("pricing.pack.getN", { n: pack.questions, price: pack.price })}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <div className="font-serif text-lg text-slate-300">
            {lang === "hi" ? (
              <>मंत्रणा<span className="gold-text"> One</span></>
            ) : (
              <>Mantrana<span className="gold-text"> One</span></>
            )}
          </div>
          <div>{t("footer.byline")} © {new Date().getFullYear()}</div>
        </div>
      </footer>
    </div>
  );
}
